import { ALL_ATTRIBUTION_KEYS } from '@/shared/constants/attribution';
import { LS_UTM_PARAMS } from '@/shared/constants/localStorage';

export type AttributionParams = Partial<Record<(typeof ALL_ATTRIBUTION_KEYS)[number], string>>;

/**
 * Type sent to backend on signup. Backend uses set-once semantics —
 * fields not present in payload are not touched on existing user records.
 */
export type AttributionPayloadForSignup = {
  [K in (typeof ALL_ATTRIBUTION_KEYS)[number] as `first_${K}`]?: string;
} & {
  ga4_client_id?: string;
};

/**
 * Capture UTM params + click IDs from URL into localStorage.
 * Overwrites previous values (last-touch semantics for the LS cache).
 * Returns the captured params for immediate use.
 */
export const captureAttributionParamsFromUrl = (): AttributionParams => {
  const urlParams = new URLSearchParams(window.location.search);
  const params: AttributionParams = {};
  let hasParams = false;

  for (const key of ALL_ATTRIBUTION_KEYS) {
    const value = urlParams.get(key);
    if (value) {
      params[key] = value;
      hasParams = true;
    }
  }

  if (hasParams) {
    try {
      window.localStorage.setItem(LS_UTM_PARAMS, JSON.stringify(params));
    } catch {}
  }

  return params;
};

/**
 * Read stored attribution params (UTMs + click IDs) from localStorage.
 * Falls back to empty object if nothing stored or JSON is invalid.
 */
export const getStoredAttributionParams = (): AttributionParams => {
  try {
    const stored = window.localStorage.getItem(LS_UTM_PARAMS);
    return stored ? (JSON.parse(stored) as AttributionParams) : {};
  } catch {
    return {};
  }
};

/**
 * Read GA4 client_id from the _ga cookie.
 * Format: GA1.1.XXXXXXXXXX.XXXXXXXXXX → client_id = last two parts joined by dot.
 */
export const getGA4ClientId = (): string | undefined => {
  try {
    const gaCookie = document.cookie.split('; ').find((row) => row.startsWith('_ga='));
    if (!gaCookie) return undefined;

    const value = gaCookie.split('=')[1];
    const parts = value.split('.');

    if (parts.length >= 4) {
      return `${parts[2]}.${parts[3]}`;
    }
  } catch {}

  return undefined;
};

/**
 * Assemble attribution payload for the signup API call.
 * Includes only fields with values — backend uses set-once semantics,
 * so missing fields don't overwrite existing values on the user record.
 */
export const getAttributionPayloadForSignup = (): AttributionPayloadForSignup => {
  const stored = getStoredAttributionParams();
  const payload: AttributionPayloadForSignup = {};

  for (const key of ALL_ATTRIBUTION_KEYS) {
    const value = stored[key];
    if (value) {
      (payload as Record<string, string>)[`first_${key}`] = value;
    }
  }

  const clientId = getGA4ClientId();
  if (clientId) payload.ga4_client_id = clientId;

  return payload;
};
